import { message } from 'antd'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import { PaginationResponse } from '../../../types/pagination-response-dto'
import { CensusFilesRequestDto } from '../../CensusManagement/api/dto/census-files-request.dto'
import { HrisCensusReportUploadDto } from './dto/hris-census-report-upload.dto'
import { PresignedUploadUrlDto } from './dto/presigned-upload-url.dto'

export const getHrisCensusReportFiles = async (
  params?: CensusFilesRequestDto
): Promise<PaginationResponse<HrisCensusReportUploadDto>> => {
  const url = '/hris-census/reports'
  const ret = await apiClient.get<
    PaginationResponse<HrisCensusReportUploadDto>
  >(url, { params: params ?? {} })
  return ret.data
}

export const getPresignedHrisReportDownloadUrl = async (
  id: string
): Promise<{ url: string }> => {
  const url = `hris-census/reports/${id}/presigned-download-url`
  const ret = await apiClient.post<{ url: string }>(url)
  return ret.data
}

export const getPresignedGeneratedFileDownloadUrl = async (
  id: string
): Promise<{ url: string }> => {
  const url = `hris-census/reports/generated-files/${id}/presigned-download-url`
  const ret = await apiClient.post<{ url: string }>(url)
  return ret.data
}

export const getPresignedUploadUrl = async ({
  clientId,
  fileName,
  censusVersion,
}: {
  clientId: string
  fileName: string
  censusVersion: number
}): Promise<PresignedUploadUrlDto> => {
  const url = 'hris-census/reports/presigned-upload-url'
  const ret = await apiClient.post<PresignedUploadUrlDto>(url, {
    clientPublicKey: clientId,
    fileName,
    censusVersion,
  })
  return ret.data
}

// upload file to s3
export const uploadHrisReport = async (presignedUrl: string, file) => {
  if (!file) {
    message.error('No file selected')
    return false
  }

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'text/csv',
      },
    })

    if (response.ok) {
      return true
    } else {
      const errorText = await response.text()
      console.error('Upload failed:', response.status, errorText)
    }
  } catch (error: any) {
    message.error('Error uploading file: ', error?.message)
    console.error('Upload failed:', error.message)
  }
  return false
}

export const processHrisReport = async ({
  fileName,
  hrisCensusReportId,
  clientPublicKey,
  censusVersion,
}: {
  fileName: string
  hrisCensusReportId: string
  clientPublicKey: string
  censusVersion: number
}) => {
  const ret = await apiClient.post('/hris-census/reports', {
    fileName,
    hrisCensusReportId,
    clientPublicKey,
    censusVersion,
  })
  return ret.data
}

export const uploadSplitHrisReport = async ({
  clientPublicKey,
  censusVersion,
  files,
}: {
  clientPublicKey: string
  censusVersion: number
  files: File[]
}) => {
  const formData = new FormData()
  formData.append('clientPublicKey', clientPublicKey)
  formData.append('censusVersion', String(censusVersion))

  files.forEach((file) => {
    formData.append('files', file)
  })

  const ret = await apiClient.post('/hris-census/reports/split', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  // 202: the split files were stored and each target is processed in the background
  return { report: ret.data, isQueued: ret.status === 202 }
}

export const submitFileToCensusV1 = async (fileId: string) => {
  const ret = await apiClient.post(
    `/hris-census/reports/generated-files/${fileId}/census-v1-submit`
  )
  return ret.data
}

export const submitFileToCensusV2 = async (filePublickey: string) => {
  const ret = await apiClient.post(
    `/hris-census/reports/generated-files/${filePublickey}/census-v2-submit`
  )
  return ret.data
}

export const createZendeskTicket = async (reportId: string) => {
  const ret = await apiClient.post(
    `/hris-census/reports/${reportId}/create-ticket`
  )
  return ret.data
}

export const fetchActiveCustomers = async (): Promise<SimpleCustomerDto[]> => {
  const res = await apiClient.get<SimpleCustomerDto[]>('/hris-census/customers')
  return res.data.filter((c) => c.isActive)
}
